import { Location, LocationState } from 'history';
import { groupBy, isEqual } from 'lodash-es';
import { stringify } from 'query-string';
import React, { FC, useCallback, useEffect, useState } from 'react';
import BeforeUnloadComponent from 'react-beforeunload-component';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';

import {
  Button,
  ConfirmDeleteModal,
  GastronomGrid,
  Modal,
  NavigateBackButton,
  SelectedCheckmark,
  SelectFoodItemModal,
  SideBySide,
  TextInput,
  Theme,
} from '../../components';
import {
  FOOD_TRAY_SIZES_PER_COLUMN,
  FoodLine,
  FoodTraySize,
  FoodTrayTemplate,
  FoodType,
  SelectedFoodTrayIndex,
} from '../../models';
import { RestoAdminLink } from '../../models/Routes';
import {
  foodTrayTemplateEditActions,
  foodTrayTemplateEditSelectors,
  foodTrayTemplatesActions,
  foodTrayTemplatesSelectors,
} from '../../redux';
import { FOOD_TRAY_INITIAL_STATE } from '../../redux/foodTrayTemplateEdit/reducer';

import './RestoAdminFoodTemplateEdit.scss';

const FoodTrayTemplateEdit: FC<RouteComponentProps<{ id: string }>> = ({ history, match, location }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const dbFoodTrayTemplate: FoodTrayTemplate | undefined = useSelector(foodTrayTemplatesSelectors.getFoodTrayTemplate);
  const isDeleteLoading: boolean = useSelector(foodTrayTemplatesSelectors.isDeleteLoading);
  const isTemplateLoading: boolean = useSelector(foodTrayTemplatesSelectors.isLoading);
  const template = useSelector(foodTrayTemplateEditSelectors.getCurrentFoodTrayTemplate);
  const hasUnsavedChanges: boolean = useSelector(foodTrayTemplateEditSelectors.hasUnsavedChanges);
  const isCreateLoading: boolean = useSelector(foodTrayTemplatesSelectors.isCreateLoading);
  const isUpdateLoading: boolean = useSelector(foodTrayTemplatesSelectors.isUpdateLoading);

  const [isDeleteTemplateModelOpen, setIsDeleteTemplateModelOpen] = useState<boolean>(false);
  const [isEditGastronomModalOpen, setIsEditGastronomModalOpen] = useState<boolean>(false);
  const [isSelectFoodTypeModalOpen, setIsSelectedFoodTypeModelOpen] = useState<boolean>(false);
  const templateId = match.params.id;
  const [selectedFoodLineIndex, setSelectedFoodLineIndex] = useState<number | null>(null);
  const [selectedFoodTrayIndex, setSelectedFoodTrayIndex] = useState<SelectedFoodTrayIndex | null>(null);
  const [selectedGastronomColumnLayout, setSelectedGastronomColumnLayout] = useState<FoodTraySize[]>(
    FOOD_TRAY_SIZES_PER_COLUMN[0],
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState<boolean>(false);

  const selectedFoodTray = template?.foodLines[selectedFoodLineIndex]?.foodTrays?.find(
    foodTray =>
      foodTray.columnIndex === selectedFoodTrayIndex.columnIndex && foodTray.rowIndex === selectedFoodTrayIndex.rowIndex,
  );

  const numberOfColumns = Object.keys(
    groupBy(template?.foodLines[selectedFoodLineIndex]?.foodTrays, foodTray => foodTray.columnIndex),
  ).length;
  const isCreateTemplate = !templateId;

  const canSave = useCallback(() => {
    if (!hasUnsavedChanges) {
      // You need to change something before you can save
      return false;
    }
    if ((template.name || '').trim().length === 0) {
      // Template name needs to be filled in
      return false;
    }
    if (template.foodLines.filter(foodLine => foodLine.name.trim().length === 0).length > 0) {
      // All food lines need to have a name
      return false;
    }
    if (template.foodLines.length === 0) {
      // You need to define at least one food line
      return false;
    }
    return true;
  }, [hasUnsavedChanges, template]);

  useEffect(() => {
    if (templateId) {
      dispatch(foodTrayTemplatesActions.getFoodTrayTemplate(templateId));
    }
  }, [templateId]);

  useEffect(() => {
    if (dbFoodTrayTemplate) {
      dispatch(foodTrayTemplateEditActions.setFoodTrayTemplate(dbFoodTrayTemplate, true));
    }
  }, [dbFoodTrayTemplate]);

  const onUnload = useCallback(
    (event: globalThis.Event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    },
    [hasUnsavedChanges],
  );

  useEffect(() => {
    // Register listener once when the component loads
    window.addEventListener('beforeunload', onUnload);

    // Remove listener when the component unloads
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [onUnload]);

  useEffect(() => {
    if (isCreateLoading || isUpdateLoading) {
      setIsSaving(true);
    }
  }, [isCreateLoading, isUpdateLoading]);

  useEffect(() => {
    if (isSaving && !isCreateLoading && !isUpdateLoading) {
      history.push(getBackState());
      setIsSaving(false);
    }
  }, [isSaving, isCreateLoading, isUpdateLoading]);

  useEffect(() => {
    if (!isDeleteLoading && isDeleteConfirmed) {
      history.push(getBackState());
    }
  }, [isDeleteLoading, isDeleteConfirmed]);

  const addFoodLine = () => {
    dispatch(foodTrayTemplateEditActions.addFoodLine());
  };

  const deleteTemplate = () => {
    dispatch(foodTrayTemplatesActions.deleteFoodTrayTemplate(templateId));
    setIsDeleteConfirmed(true);
  };

  const openGastronomLayoutSelectionModal = () => {
    setSelectedGastronomColumnLayout(
      template.foodLines[selectedFoodLineIndex].foodTrays
        .filter(foodTray => foodTray.columnIndex === selectedFoodTrayIndex.columnIndex)
        .map(foodTray => foodTray.size),
    );
    setIsEditGastronomModalOpen(true);
  };

  const applyFoodTrayColumnLayout = () => {
    dispatch(
      foodTrayTemplateEditActions.setFoodTraysForColumn(
        selectedGastronomColumnLayout,
        selectedFoodLineIndex,
        selectedFoodTrayIndex.columnIndex,
      ),
    );
    setIsEditGastronomModalOpen(false);
  };

  const setFoodTypeForSelectedFoodTray = (selectedFoodType: FoodType) => {
    dispatch(
      foodTrayTemplateEditActions.setFoodTrayProp(
        'defaultFoodType',
        selectedFoodType,
        selectedFoodLineIndex,
        selectedFoodTrayIndex.columnIndex,
        selectedFoodTrayIndex.rowIndex,
      ),
    );
  };

  const saveTemplate = () => {
    if (templateId) {
      dispatch(foodTrayTemplatesActions.updateFoodTrayTemplate(templateId, template));
    } else {
      dispatch(foodTrayTemplatesActions.createFoodTrayTemplate(template));
    }
  };

  const handleAddColumn = (columnIndex: number, foodLineIndex: number) => {
    dispatch(foodTrayTemplateEditActions.addFoodTrayColumn([{ ...FOOD_TRAY_INITIAL_STATE }], foodLineIndex, columnIndex));
    setSelectedFoodTrayIndex(oldSelection => ({
      ...oldSelection,
      columnIndex,
    }));
  };

  const handleDeleteColumn = (columnIndex: number, foodLineIndex: number) => {
    dispatch(foodTrayTemplateEditActions.removeFoodTrayColumn(foodLineIndex, columnIndex));
    setSelectedFoodTrayIndex(oldSelection => {
      if (oldSelection.columnIndex < numberOfColumns - 1) {
        // User deleted a column => select the column that takes its place on the same index
        return { columnIndex: oldSelection.columnIndex, rowIndex: 0 };
      }
      // User has deleted the last column in the food line => select the last column after the delete
      return { columnIndex: numberOfColumns - 2, rowIndex: 0 };
    });
  };

  const getBackState = useCallback((): Location<LocationState> => {
    let path: string;
    let queryParams = '';
    let prevState = location?.state as Location<LocationState>;
    if (prevState?.pathname) {
      path = prevState?.pathname;
      queryParams = (prevState?.search || '').replace('?', '');
    } else {
      path = RestoAdminLink.FOOD_TRAY_TEMPLATES;
      if (template?.department?.id) {
        queryParams = stringify({
          department: template?.department?.id,
        });
      }
    }

    return {
      pathname: path,
      search: queryParams,
      state: location,
      hash: undefined,
    };
  }, [template]);

  const getBackStateLink = useCallback((): string => {
    const state = getBackState();
    if (state.search) {
      return state.pathname + '?' + state.search;
    } else {
      return state.pathname;
    }
  }, [getBackState]);

  const renderFoodLine = (foodLine: FoodLine, foodLineIndex: number) => {
    return (
      <div className='food-line' key={'food-line-' + (foodLine.id || foodLine.index)}>
        <SideBySide>
          <TextInput
            label={t('lblFoodLineTitle')}
            value={foodLine.name}
            onChange={newName => dispatch(foodTrayTemplateEditActions.setFoodLineProp('name', newName, foodLineIndex))}
            placeholder={t('phFoodLineName')}
            className='food-line-title-input'
          />
          <Button
            theme={Theme.TRANSPARANT}
            icon='Trashcan'
            onClickCallback={() => dispatch(foodTrayTemplateEditActions.removeFoodLine(foodLineIndex))}>
            {t('lblDeleteFoodLine')}
          </Button>
        </SideBySide>
        <GastronomGrid
          isAdmin
          foodTrays={foodLine.foodTrays}
          setSelectedFoodTray={newFoodTray => {
            if (newFoodTray === selectedFoodTray) {
              setIsSelectedFoodTypeModelOpen(true);
            } else {
              setSelectedFoodLineIndex(foodLineIndex);
              setSelectedFoodTrayIndex({
                columnIndex: newFoodTray.columnIndex,
                rowIndex: newFoodTray.rowIndex,
              });
            }
          }}
          selectedFoodTray={foodLineIndex === selectedFoodLineIndex && selectedFoodTrayIndex}
          isOverview
          isTabbable
          onAddColumn={columnIndex => handleAddColumn(columnIndex, foodLineIndex)}
          onDeleteColumn={columnIndex => handleDeleteColumn(columnIndex, foodLineIndex)}
        />
        <div className='bottom-buttons'>
          {!!selectedFoodTrayIndex && selectedFoodLineIndex === foodLineIndex && (
            <>
              <Button theme={Theme.SECONDARY} onClickCallback={openGastronomLayoutSelectionModal} icon='Gastronom'>
                {t('btnSelectGastronomSetup')}
              </Button>
              <Button theme={Theme.SECONDARY} onClickCallback={() => setIsSelectedFoodTypeModelOpen(true)} icon='Cutlery'>
                {t('btnSelectFoodItems')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTemplate = () => {
    return (
      <>
        <div className='food-template-edit content extra-margin'>
          <section>
            <SideBySide>
              <NavigateBackButton label={'lblBackToOverview'} link={getBackState()} />
              {!isCreateTemplate && (
                <Button theme={Theme.INVERSE_DESTRUCTIVE} onClickCallback={() => setIsDeleteTemplateModelOpen(true)}>
                  {t('btnDeleteTemplate')}
                </Button>
              )}
            </SideBySide>
            <h1>{template.name}</h1>
            <TextInput
              className='template-title-input'
              label={t('lblFoodTrayTemplateTitle')}
              placeholder={t('phTemplateName')}
              value={template?.name || ''}
              onChange={newTitle => {
                dispatch(foodTrayTemplateEditActions.setFoodTrayTemplateProp('name', newTitle));
              }}
            />
            {(template.foodLines || []).map(renderFoodLine)}
          </section>
          <section>
            <Button theme={Theme.INVERSE} onClickCallback={addFoodLine} icon='Add'>
              {t('btnAddFoodLine')}
            </Button>
          </section>
        </div>
        <div className='bottom-buttons'>
          <Button theme={Theme.INVERSE} link={getBackStateLink()}>
            {t('btnCancel')}
          </Button>
          <Button
            theme={Theme.SECONDARY}
            onClickCallback={saveTemplate}
            isDisabled={!canSave()}
            isLoading={isCreateLoading || isUpdateLoading}>
            {t('btnSave')}
          </Button>
        </div>
        <ConfirmDeleteModal
          isOpen={isDeleteTemplateModelOpen}
          closeModal={() => setIsDeleteTemplateModelOpen(false)}
          onDeleteConfirmed={deleteTemplate}
          title={'titleDeleteFoodTrayTemplate'}
          description={'lblDeleteFoodTrayTemplate'}
          checkboxDescription={t('lblDeleteFoodTrayTemplateConfirm', { name: template.name })}
          isDeleteLoading={isDeleteLoading}
        />
        <Modal
          closeModal={() => setIsEditGastronomModalOpen(false)}
          className='food-tray-layout-selection-modal'
          isOpen={isEditGastronomModalOpen}
          title={t('titleSelectGastronomSetup')}>
          <section className='food-tray-size-wrapper'>
            {FOOD_TRAY_SIZES_PER_COLUMN.map((foodTraySizes, columnIndex) => {
              return (
                <button
                  className={`food-tray-size-column ${isEqual(selectedGastronomColumnLayout, foodTraySizes) ? 'selected' : ''}`}
                  key={'food-tray-size-column-' + columnIndex}
                  onClick={() => setSelectedGastronomColumnLayout(foodTraySizes)}>
                  <SelectedCheckmark />
                  {foodTraySizes.map((foodTraySize, rowIndex) => {
                    return (
                      <span
                        className={`food-tray-size ${foodTraySize === FoodTraySize.TWO_THIRDS ? 'double-size' : ''}`}
                        key={`food-tray-size-${columnIndex}-${rowIndex}-${foodTraySize}`}>
                        {foodTraySize}
                      </span>
                    );
                  })}
                </button>
              );
            })}
          </section>
          <div className='buttons'>
            <Button theme={Theme.INVERSE} onClickCallback={() => setIsEditGastronomModalOpen(false)} wide>
              {t('lblCancel')}
            </Button>
            <Button theme={Theme.SECONDARY} onClickCallback={applyFoodTrayColumnLayout} wide>
              {t('lblConfirm')}
            </Button>
          </div>
        </Modal>
        <SelectFoodItemModal
          isOpen={isSelectFoodTypeModalOpen}
          closeModal={() => setIsSelectedFoodTypeModelOpen(false)}
          onSelectFoodType={setFoodTypeForSelectedFoodTray}
          defaultSelectedFoodType={selectedFoodTray?.foodItem?.overrideFoodType || selectedFoodTray?.defaultFoodType}
        />
      </>
    );
  };

  const renderSkeleton = () => {
    return (
      <>
        <div className='food-template-edit content extra-margin'>
          <section>
            <SideBySide>
              <Skeleton width={146} height={34} />
              {!isCreateTemplate && <Skeleton width={144} height={44} />}
            </SideBySide>
            <h1>
              <Skeleton width={300} height={80} />
            </h1>
            <Skeleton width={320} height={72} />
            <Skeleton width={10000} height={536} />
          </section>
          <Skeleton width={144} height={44} />
        </div>
        <div className='bottom-buttons'>
          <Skeleton width={144} height={44} />
          <Skeleton width={144} height={44} />
        </div>
      </>
    );
  };

  return (
    <BeforeUnloadComponent
      blockRoute={hasUnsavedChanges}
      modalComponentHandler={({ handleModalLeave, handleModalCancel }) => {
        return (
          <ConfirmDeleteModal
            isOpen={true}
            title='titleUnsavedChanges'
            description='lblUnsavedChanges'
            closeModal={handleModalCancel}
            //this is a temporary fix, the BeforeUnloadComponent library is broken.
            onDeleteConfirmed={() => {
              handleModalLeave();
              history.replace(getBackStateLink());
            }}
            cancelLabel='lblRemain'
            confirmLabel='lblLeave'
            confirmButtonTheme={Theme.SECONDARY}
          />
        );
      }}>
      {isTemplateLoading ? renderSkeleton() : renderTemplate()}
    </BeforeUnloadComponent>
  );
};

export default withRouter(FoodTrayTemplateEdit);
